import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Cube } from '@phosphor-icons/react';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ComposeMessageIcon from '../../../../../../assets/svg/composeMessage.svg';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../hooks/global/typedReduxHooks';
import {
  IRFIConversation,
  Sender,
  setActiveRFIConversation,
  setComposeOptions,
  setRFIConversations,
  setUnreadMessages,
} from '../../../../../../slices/rfiMessageSlice';
import RFIMessages from '../../../../../common/rfiMessages';

import { getMapValue } from '~/assets/utils/helpers';
import { intlFormatRfiStatus } from '~/constants/reportsRfiIntl';
import { RequestInformationItem } from '~/services/api/reports/types';
import requestInformationService from '~/services/requestInformationService';
import { setRfiMessages } from '~/slices/reportDataSlice';

interface MessageRFITabContentProps {
  list: RequestInformationItem[];
}

export const RFI_STATUS_CLOSED = 4;

const MessageRFITabContent = ({ list } : MessageRFITabContentProps) => {
  const [isComposeMessageOpen, setIsComposeMessageOpen] = useState(false);
  const { composeOptions, rfiConversations, activeRFIConversation, loadingStates, unreadMessages } =
    useAppSelector((state) => state.rfiMessage);
  const dispatch = useAppDispatch();  
  const allLoadingFinished = Object.values(loadingStates).every(loadingState => !loadingState);

  useEffect(() => {
    const listRFIConversations = list
      .filter((item) => item.rfi_conversation !== null)
      .map((item) => {
        return { ...item.rfi_conversation, messages: [], status: item.status };
      });
    dispatch(setComposeOptions(listRFIConversations));
    dispatch(setRFIConversations(listRFIConversations));
  }, [dispatch, list]);


  useEffect(() => {
    if (allLoadingFinished) {
      dispatch(setRfiMessages(rfiConversations));
    }
  }, [allLoadingFinished, dispatch, rfiConversations]);

  const truncateText = (text: string, maxLength: number) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };
  
  const getMessageContent = (rfiConversation: IRFIConversation) => {
    const latestMessage = Array.isArray(rfiConversation.messages) && rfiConversation.messages.length > 0
    ? rfiConversation.messages[rfiConversation.messages.length - 1]
    : {
        content: '',
      };

    if (!allLoadingFinished || loadingStates[rfiConversation.id] === undefined && latestMessage.content !== '') {
      return <Skeleton variant="text" width={100} />;
    }
  
    if (latestMessage.content !== '') {
      return truncateText(latestMessage.content, 55);
    }
  };  
  
  const getMessageCreatedAt = (rfiConversation: IRFIConversation) => {
    const latestMessage = Array.isArray(rfiConversation.messages) && rfiConversation.messages.length > 0
    ? rfiConversation.messages[rfiConversation.messages.length - 1]
    : {
        createdAt: 0,
      };

    if (!allLoadingFinished || loadingStates[rfiConversation.id] === undefined && latestMessage.createdAt !== 0) {
      return <Skeleton variant="text" width={100} />;
    }
  
    if (latestMessage.createdAt !== 0) {
      return latestMessage.createdAt !== 0
        && new Date(
            latestMessage.createdAt,
          ).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          });
    }
  
    return '';
  };
  
  const renderRFIMessages = useCallback(() => {
    return rfiConversations.map((data, index) => (
      <RFIMessages
        key={index}
        rfiConversation={data}
        sender={Sender.CASE_MANAGER}
        containerPadding={2}
        displayBackButton
      />
    ));
  }, [rfiConversations]);

  if (!allLoadingFinished && isEmpty(rfiConversations)) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
      }}
      pt={1}
    >
      {/* Render existing rfi conversations to maintain websocket connection */}
      {/* Will display depending on activeRFIConversation */}
      
      {renderRFIMessages()}
      
      {!activeRFIConversation && (
        <>
          {isComposeMessageOpen && (
            <Stack>
              <Stack direction='row'>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    lineHeight: '125%',
                  }}
                >
                  <FormattedMessage defaultMessage='Select RFI' />
                </Typography>
              </Stack>
              <Stack direction='row'>
                <Autocomplete
                  disablePortal
                  options={composeOptions}
                  sx={{ width: '100%' }}
                  renderInput={(params) => <TextField {...params} />}
                  getOptionLabel={(option) => option.recipient_full_name}
                  renderOption={(props, option) => (
                    <Box component='li' {...props} key={option.id}>
                      <Box display='flex' flex={1} justifyContent='space-between'>
                        <Box flex={1}>
                          <Typography fontSize={14} fontWeight={600}>{option.recipient_full_name}</Typography>
                          <Typography fontSize={12}><strong>Email: </strong>{option.recipient_email}</Typography>
                          <Typography fontSize={12}><strong>Subject: </strong>{option.conversation_subject}</Typography>
                        </Box>
                        <Box display='flex' flex={1} justifyContent='flex-end'>
                          {option.status === RFI_STATUS_CLOSED && (
                            <Chip color='error' label={intlFormatRfiStatus(getMapValue(option.status))} size='small'/>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(_, value) => {
                    if (value) {
                      requestInformationService.rfiConversationIsRead(value.id).then(() => {
                        dispatch(setActiveRFIConversation(value.id));
                        dispatch(setUnreadMessages({ conversationId: value.id, number: 0 }));
                      });
                    }
                  }}
                  onClose={() => setIsComposeMessageOpen(false)}
                />
              </Stack>
            </Stack>
          )}
          {!isComposeMessageOpen && (
            <>
              {!isEmpty(rfiConversations) && (
                <Stack overflow='auto' maxHeight={'80%'}>
                  {rfiConversations.map((rfiConversation, index) => {
                    return (
                      <Stack
                        direction='row'
                        key={rfiConversation.id}
                        gap={2}
                        sx={{
                          paddingX: '4px',
                          paddingY: '16px',
                          borderBottom:
                            index === rfiConversations.length - 1
                              ? 'none'
                              : '1px solid #D6D6D6',
                          cursor: Object.keys(loadingStates).length !== 0 && allLoadingFinished ? 'pointer' : '',
                          '&:hover': {
                            backgroundColor: Object.keys(loadingStates).length !== 0 && allLoadingFinished ? '#F5F5F5' : '',
                          },
                        }}
                        onClick={() => {
                            if (Object.keys(loadingStates).length !== 0 && allLoadingFinished) {
                              requestInformationService.rfiConversationIsRead(rfiConversation.id).then(() => {
                                dispatch(setActiveRFIConversation(rfiConversation.id));
                                dispatch(setUnreadMessages({ conversationId: rfiConversation.id, number: 0 }));
                              });
                            }
                          }
                        }
                      >
                        <Stack position='relative'>
                          <Badge color="error" overlap="circular" badgeContent={allLoadingFinished ? unreadMessages[rfiConversation.id] : 0} max={10}>
                            <Box
                              sx={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '50%',
                                backgroundColor: () =>
                                  (index + 1) % 2 ? '#2449FC' : '#03FF75',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: () =>
                                  (index + 1) % 2 ? '#F6F6F6' : '#333',
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: '700',
                                lineHeight: '24px',
                              }}
                            >
                              {rfiConversation.recipient_full_name[0]}
                            </Box>
                          </Badge>
                        </Stack>
                        <Stack flexGrow={1}>
                          <Stack direction='row' justifyContent='space-between'>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Stack>
                                {rfiConversation.recipient_full_name}
                              </Stack>
                              <Stack>
                                {rfiConversation.status === RFI_STATUS_CLOSED && (
                                  <Chip color='error' label={intlFormatRfiStatus(getMapValue(rfiConversation.status))} size='small'/>
                                )}
                              </Stack>
                            </Stack>
                            <Stack>
                              {getMessageCreatedAt(rfiConversation)}
                            </Stack>
                          </Stack>
                          <Stack direction='row' sx={{ width: '100%', maxWidth: '500px' }}>
                            {getMessageContent(rfiConversation)}
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              {isEmpty(rfiConversations) && (
                <Stack
                  id='emptyhere'
                  height='100%'
                  width='100%'
                  justifyContent='center'
                  alignContent='center'
                  sx={{ color: (theme) => theme.palette.neutralBlackBg[500] }}
                >
                  <Stack direction='row' justifyContent='center'>
                    <Cube size={91} />
                  </Stack>
                  <Stack direction='row' justifyContent='center'>
                    <FormattedMessage defaultMessage="Your inbox appears to be clear, let's begin a conversation..." />
                  </Stack>
                </Stack>
              )}
            </>
          )}
          {!isEmpty(composeOptions) && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '0',
                right: '0',
              }}
            >
              {Object.keys(loadingStates).length !== 0 && allLoadingFinished 
                ? (
                    <Button
                      variant='contained'
                      sx={{
                        display: 'inline-flex',
                        padding: '12px',
                        gap: '8px',
                        bgcolor: '#2449FC',
                        textTransform: 'none',
                        borderRadius: '12px',
                        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.20)',
                      }}
                      onClick={() => setIsComposeMessageOpen((current) => !current)}
                    >
                      <Box
                        component='img'
                        sx={{ height: '20px' }}
                        src={ComposeMessageIcon}
                      ></Box>
                      <Typography fontSize={14}>
                        <FormattedMessage defaultMessage='Compose' />
                      </Typography>
                    </Button>
                  )
                : ( <Skeleton variant="rectangular" width={100} height={40} /> )
              }
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MessageRFITabContent;
